/**
 * SavedPlotsPanel — lists saved plots with thumbnails.
 * Clicking a plot opens a full-size rendered view within this panel.
 * Clicking a data point on the chart navigates to the Detail panel
 * so the user can inspect the run that produced it.
 */

import { useState, useCallback, useEffect, useMemo } from "react";
import { Box, Heading, Text } from "@radix-ui/themes";
import { useTheme } from "../../context/ThemeContext";
import { useProvenanceContext } from "../../context/ProvenanceContext";
import { useSavedPlots, useDeletePlot } from "../../hooks/useSavedPlots";
import { usePlotComparison } from "../../hooks/usePlotComparison";
import { DataViewToggle } from "./DataViewToggle";
import { getRenderer } from "../plot-builder/renderers";
import {
  createDefaultConfig,
  getDefaultEngine,
  CHART_TYPE_ICONS,
} from "../plot-builder/types";
import type { PlotBuilderConfig, ChartType } from "../plot-builder/types";
import type { SavedPlot } from "../../api/plots";
import { ProvenancePopover, useDataPointContextMenu, DataPointContextMenu } from "../shared/DataPointContextMenu";

interface Props {
  projectId: string;
  onLoadPlot?: (config: PlotBuilderConfig, data: Record<string, unknown>[]) => void;
}

const CHART_TYPE_COLORS: Record<string, string> = {
  line: "#3b82f6",
  scatter: "#22c55e",
  bar: "#f59e0b",
  histogram: "#8b5cf6",
  heatmap: "#ef4444",
  polar: "#06b6d4",
};

function ScopeBadge({ scope, isLight }: { scope: string; isLight: boolean }) {
  const isBranch = scope === "branch";
  return (
    <span
      style={{
        fontSize: 9,
        fontWeight: 600,
        padding: "1px 5px",
        borderRadius: 3,
        background: isBranch
          ? (isLight ? "#E8F0FE" : "#1a2a3e")
          : (isLight ? "#E6F4ED" : "#0A3822"),
        color: isBranch
          ? (isLight ? "#1a56db" : "#6ea8fe")
          : (isLight ? "#00643C" : "#4ade80"),
        whiteSpace: "nowrap",
      }}
    >
      {isBranch ? "This branch" : "All branches"}
    </span>
  );
}

const THUMB_WIDTH = 180;
const THUMB_HEIGHT = 120;
const MAX_RENDERED_THUMBNAILS = 20;

/** Build DAG-style mini config + data for a saved plot (same logic as PlotNode). */
function usePlotThumbnailRender(plot: SavedPlot) {
  return useMemo(() => {
    const rawConfig = plot.config as Record<string, unknown> | undefined;
    const snapshot = Array.isArray(plot.data_snapshot) ? plot.data_snapshot : [];
    if (!rawConfig || snapshot.length === 0) {
      return { canRender: false, config: null, plotData: [] as Record<string, unknown>[] };
    }
    const chartTypeVal = (plot.chart_type as ChartType) ?? "line";
    const engine = (rawConfig.engine as PlotBuilderConfig["engine"]) ?? getDefaultEngine(chartTypeVal);
    const config: PlotBuilderConfig = {
      ...createDefaultConfig(),
      ...rawConfig,
      chartType: chartTypeVal,
      engine,
      title: "",
      showLegend: false,
      plotPadding: 8,
      titleSize: 10,
    } as PlotBuilderConfig;
    const hasAxes =
      config.xAxis?.field &&
      config.yAxis?.field &&
      snapshot.some(
        (row) =>
          row[config.xAxis!.field] != null && row[config.yAxis!.field] != null,
      );
    return {
      canRender: !!hasAxes,
      config,
      plotData: snapshot as Record<string, unknown>[],
    };
  }, [plot.config, plot.data_snapshot, plot.chart_type]);
}

function PlotCard({
  plot,
  theme,
  isLight,
  onSelect,
  onDelete,
}: {
  plot: SavedPlot;
  theme: "light" | "dark";
  isLight: boolean;
  onSelect: () => void;
  onDelete: () => void;
}) {
  const icon = CHART_TYPE_ICONS[plot.chart_type as ChartType] ?? "📊";
  const { canRender, config, plotData } = usePlotThumbnailRender(plot);
  const Renderer = useMemo(
    () => (config ? getRenderer(config.engine) : null),
    [config?.engine],
  );
  const date = new Date(plot.created_at);
  const timeStr = date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
  const rowCount = Array.isArray(plot.data_snapshot) ? plot.data_snapshot.length : 0;
  const accentColor = CHART_TYPE_COLORS[plot.chart_type as string] ?? "#00643C";

  return (
    <div
      onClick={onSelect}
      style={{
        borderRadius: 10,
        border: `1px solid ${isLight ? "#d0d5d2" : "#2a302c"}`,
        background: isLight ? "#fff" : "#1c211e",
        cursor: "pointer",
        transition: "border-color 0.2s, box-shadow 0.2s, transform 0.2s",
        overflow: "hidden",
        position: "relative",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = accentColor;
        e.currentTarget.style.boxShadow = `0 4px 16px ${isLight ? "rgba(0,0,0,0.1)" : "rgba(0,0,0,0.3)"}`;
        e.currentTarget.style.transform = "translateY(-2px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = isLight ? "#d0d5d2" : "#2a302c";
        e.currentTarget.style.boxShadow = "none";
        e.currentTarget.style.transform = "none";
      }}
    >
      {/* Accent bar */}
      <div style={{ height: 3, background: accentColor }} />

      {/* Thumbnail area — same live mini chart as DAG PlotNode */}
      <div
        style={{
          height: THUMB_HEIGHT,
          background: isLight ? "#f5f5f4" : "#161a18",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          boxShadow: `inset 0 1px 3px ${isLight ? "rgba(0,0,0,0.06)" : "rgba(0,0,0,0.2)"}`,
        }}
      >
        {canRender && config && Renderer ? (
          <div
            style={{
              width: THUMB_WIDTH,
              height: THUMB_HEIGHT,
              minHeight: THUMB_HEIGHT,
              position: "relative",
            }}
          >
            <Renderer
              data={plotData}
              config={config}
              theme={theme}
              mini
              width={THUMB_WIDTH}
              height={THUMB_HEIGHT}
            />
          </div>
        ) : plot.image_url ? (
          <img
            src={plot.image_url}
            alt={plot.title}
            style={{
              width: THUMB_WIDTH,
              height: THUMB_HEIGHT,
              objectFit: "contain",
            }}
          />
        ) : (
          <span style={{ fontSize: 36, opacity: 0.3 }}>{icon}</span>
        )}
      </div>

      {/* Card info */}
      <div style={{ padding: "8px 10px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 5, minWidth: 0, flex: 1 }}>
            <span style={{ fontSize: 13, flexShrink: 0 }}>{icon}</span>
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: isLight ? "#111813" : "#f0f2f0",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                minWidth: 0,
              }}
            >
              {plot.title}
            </div>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            title="Delete plot"
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 4,
              fontSize: 12,
              opacity: 0.3,
              color: isLight ? "#111813" : "#f0f2f0",
              flexShrink: 0,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.3")}
          >
            ✕
          </button>
        </div>
        <div
          style={{
            fontSize: 10,
            opacity: 0.45,
            marginTop: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>{rowCount} rows</span>
          <ScopeBadge scope={plot.scope} isLight={isLight} />
        </div>
        <div style={{ fontSize: 10, opacity: 0.35, marginTop: 2, textAlign: "right" }}>
          {timeStr}
        </div>
      </div>
    </div>
  );
}

function PlotDetailView({
  plot,
  theme,
  isLight,
  onBack,
  onEdit,
  onPointClick,
  selectedNodeId,
  projectId,
  branch,
}: {
  plot: SavedPlot;
  theme: "light" | "dark";
  isLight: boolean;
  onBack: () => void;
  onEdit: () => void;
  onPointClick?: (row: Record<string, unknown>) => void;
  selectedNodeId?: string | null;
  projectId: string;
  branch: string;
}) {
  const baseConfig = plot.config as unknown as PlotBuilderConfig;
  const Renderer = getRenderer(baseConfig.engine);

  const {
    hasChanges,
    viewMode,
    setViewMode,
    displayData,
    displayConfig,
    snapshotRefArea,
    liveRefArea,
    refAreaUnits,
  } = usePlotComparison(plot, projectId, branch);

  const chartData = useMemo(() => {
    const rows = displayData;
    const sourceIds = Array.isArray(plot.source_node_ids) ? plot.source_node_ids : [];
    const hasNodeIds = rows.some((r) => r != null && "_node_id" in r);
    if (hasNodeIds || sourceIds.length === 0) return rows;
    return rows.map((row, i) => ({
      ...row,
      _node_id: sourceIds[i] ?? row._node_id,
    }));
  }, [displayData, plot.source_node_ids]);

  const rendererConfig = displayConfig as unknown as PlotBuilderConfig;
  const paneCtxMenu = useDataPointContextMenu(chartData);

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "10px 12px",
          borderBottom: `1px solid ${isLight ? "#e6e9e7" : "#2a302c"}`,
          flexShrink: 0,
        }}
      >
        <button
          onClick={onBack}
          style={{
            background: "none",
            border: `1px solid ${isLight ? "#d0d5d2" : "#363d39"}`,
            borderRadius: 5,
            padding: "4px 10px",
            fontSize: 11,
            color: isLight ? "#111813" : "#d1d9d3",
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          ← Back
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: isLight ? "#111813" : "#f0f2f0",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {plot.title}
          </div>
          <div style={{ fontSize: 10, opacity: 0.5, display: "flex", alignItems: "center", gap: 6 }}>
            {plot.chart_type} · {Array.isArray(plot.data_snapshot) ? plot.data_snapshot.length : 0} rows
            <ScopeBadge scope={plot.scope} isLight={isLight} />
          </div>
        </div>
        {hasChanges && snapshotRefArea !== null && liveRefArea !== null && (
          <DataViewToggle
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            snapshotRefArea={snapshotRefArea}
            liveRefArea={liveRefArea}
            units={refAreaUnits}
            isLight={isLight}
          />
        )}
        <ProvenancePopover data={chartData} theme={theme} />
        <button
          onClick={onEdit}
          style={{
            background: "#00643C",
            border: "none",
            borderRadius: 5,
            padding: "5px 12px",
            fontSize: 11,
            fontWeight: 600,
            color: "#fff",
            cursor: "pointer",
            fontFamily: "inherit",
            whiteSpace: "nowrap",
          }}
        >
          Edit in Plot Builder
        </button>
      </div>

      {/* Chart */}
      <div
        style={{ flex: 1, minHeight: 0 }}
        onContextMenu={paneCtxMenu.handleContextMenu}
      >
        <Renderer data={chartData} config={rendererConfig} theme={theme} onPointClick={onPointClick} selectedNodeId={selectedNodeId} />
        {paneCtxMenu.menu && <DataPointContextMenu menu={paneCtxMenu.menu} onClose={paneCtxMenu.close} />}
      </div>
    </div>
  );
}

export default function SavedPlotsPanel({ projectId, onLoadPlot }: Props) {
  const { theme } = useTheme();
  const { selectArtifact, selectedArtifactId, currentBranch } = useProvenanceContext();
  const { data: plots, isLoading } = useSavedPlots(projectId, currentBranch);
  const deleteMutation = useDeletePlot(projectId, currentBranch);
  const isLight = theme === "light";
  const [viewingPlotId, setViewingPlotId] = useState<string | null>(null);

  useEffect(() => {
    if (selectedArtifactId && plots?.some((p) => p.id === selectedArtifactId)) {
      setViewingPlotId(selectedArtifactId);
    }
  }, [selectedArtifactId, plots]);

  const handlePointClick = useCallback(
    (row: Record<string, unknown>) => {
      const nodeId = row._node_id as string | undefined;
      if (nodeId) selectArtifact(nodeId);
    },
    [selectArtifact],
  );

  const viewingPlot = plots?.find((p) => p.id === viewingPlotId);

  const handleEdit = useCallback(
    (plot: SavedPlot) => {
      onLoadPlot?.(plot.config as unknown as PlotBuilderConfig, plot.data_snapshot);
    },
    [onLoadPlot],
  );

  if (isLoading) {
    return (
      <Box p="3">
        <Text color="gray">Loading saved plots...</Text>
      </Box>
    );
  }

  if (viewingPlot) {
    return (
      <PlotDetailView
        plot={viewingPlot}
        theme={theme}
        isLight={isLight}
        onBack={() => setViewingPlotId(null)}
        onEdit={() => handleEdit(viewingPlot)}
        onPointClick={handlePointClick}
        selectedNodeId={selectedArtifactId}
        projectId={projectId}
        branch={currentBranch}
      />
    );
  }

  if (!plots?.length) {
    return (
      <Box p="3" style={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <Heading size="4" mb="3">Saved Plots</Heading>
        <Text color="gray" size="2">
          No saved plots yet. Use the Plot Builder to create and save one.
        </Text>
      </Box>
    );
  }

  return (
    <Box p="3" style={{ height: "100%", overflow: "auto" }}>
      <Heading size="4" mb="3">Saved Plots</Heading>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
          gap: 10,
        }}
      >
        {plots.slice(0, MAX_RENDERED_THUMBNAILS).map((plot) => (
          <PlotCard
            key={plot.id}
            plot={plot}
            theme={theme}
            isLight={isLight}
            onSelect={() => setViewingPlotId(plot.id)}
            onDelete={() => deleteMutation.mutate(plot.id)}
          />
        ))}
        {plots.length > MAX_RENDERED_THUMBNAILS && (
          <Text size="2" color="gray" style={{ gridColumn: "1 / -1", textAlign: "center", padding: 8 }}>
            Showing {MAX_RENDERED_THUMBNAILS} of {plots.length} plots. Open a plot to see it full-size.
          </Text>
        )}
      </div>
    </Box>
  );
}
